import { useEffect, useState } from "react";
import { CalendarDays, Users, Briefcase, Scissors, Receipt, Mail, MessageCircle, Instagram, Copy, Check } from "lucide-react";
import toast from "react-hot-toast";
import { getUserById } from "../business/UserBusiness";
import { Drawer } from "./ui/Drawer";
import { Avatar } from "./ui/Avatar";
import { PlanBadge } from "./ui/Badge";
import { StatusPill } from "./ui/StatusPill";
import { PLAN_META, getEffectivePlan, getPlanSourceLabel } from "../lib/planMeta";
import {
  BUSINESS_AREA_LABELS, ACQUISITION_LABELS, ROLE_LABELS, countOf, formatCurrency, formatDate, formatRelative,
  formatPhone, getPaymentLabel, getRenewalDate,
} from "../lib/userInsights";

const Section = ({ title, children }) => (
  <section className="px-5 py-4">
    <h4 className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{title}</h4>
    <dl className="space-y-2.5">{children}</dl>
  </section>
);

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4">
    <dt className="flex-shrink-0 text-sm text-gray-500">{label}</dt>
    <dd className="min-w-0 text-right text-sm font-medium text-gray-900">{children ?? <span className="font-normal text-gray-400">—</span>}</dd>
  </div>
);

const UsageTile = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-subtle px-3 py-2.5">
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <p className="tabular mt-1 text-lg font-semibold text-gray-900">{value}</p>
  </div>
);

const QuickLink = ({ href, icon: Icon, label, onClick, external }) => {
  const className = "inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-card transition hover:bg-gray-50";
  if (onClick) {
    return <button type="button" onClick={onClick} className={className}><Icon className="h-3.5 w-3.5" /> {label}</button>;
  }
  return (
    <a href={href} className={className} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </a>
  );
};

// Detalhes de uma usuária num painel lateral. Recebe o item da lista (já tem contagens e
// assinatura) e completa com GET /user/:id (avatar, endereço, instagram...).
// `footer` recebe a pessoa carregada e devolve os botões de ação da tela que abriu.
export const UserDetailsDrawer = ({ person, onClose, footer }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!person?._id) {
      setDetails(null);
      return undefined;
    }
    let cancelled = false;
    setDetails(null);
    setLoading(true);
    getUserById(person._id)
      .then((res) => { if (!cancelled) setDetails(res?.data?.user || null); })
      .catch(() => { if (!cancelled) toast.error("Erro ao carregar detalhes do usuário"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [person?._id]);

  // O detalhe devolve clients/employees como arrays de id; o resto vem da lista
  const p = person ? { ...person, ...(details || {}), _id: person._id } : null;

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(p._id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const plan = p ? getEffectivePlan(p) : "free";
  const renewal = p ? getRenewalDate(p) : null;
  const areas = (p?.businessAreas || []).map((a) => (a === "outro" && p.businessAreaOther ? p.businessAreaOther : BUSINESS_AREA_LABELS[a] || a));
  const instagram = p?.instagram ? String(p.instagram).replace(/^@/, "") : null;

  return (
    <Drawer isOpen={!!person} onClose={onClose} title="Detalhes do usuário" footer={p && footer ? footer(p) : null}>
      {p && (
        <>
          <div className="px-5 pb-4 pt-5">
            <div className="flex items-start gap-4">
              <Avatar name={p.username} src={p.avatarUrl} size="lg" />
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-lg font-semibold text-gray-900">{p.username}</h3>
                <p className="truncate text-sm text-gray-500">{p.businessName || p.email}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <PlanBadge plan={plan} meta={PLAN_META} />
                  {p.isAmbassador && <StatusPill tone="violet">Embaixadora</StatusPill>}
                  {p.dispatchesDisabled && <StatusPill tone="amber">Sem disparos</StatusPill>}
                  {p.role && p.role !== "user" && <StatusPill tone={p.role === "admin" ? "red" : "blue"}>{ROLE_LABELS[p.role] || p.role}</StatusPill>}
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {p.phone && <QuickLink href={`https://wa.me/${String(p.phone).replace(/\D/g, "")}`} icon={MessageCircle} label="WhatsApp" external />}
              {p.email && <QuickLink href={`mailto:${p.email}`} icon={Mail} label="E-mail" />}
              {instagram && <QuickLink href={`https://instagram.com/${instagram}`} icon={Instagram} label="Instagram" external />}
              <QuickLink onClick={copyId} icon={copied ? Check : Copy} label={copied ? "Copiado" : "Copiar ID"} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 px-5 pb-2">
            <UsageTile icon={CalendarDays} label="Agendamentos" value={countOf(person, "appointments")} />
            <UsageTile icon={Users} label="Clientes" value={countOf(p, "clients")} />
            <UsageTile icon={Receipt} label="Lançamentos" value={countOf(person, "transactions")} />
            <UsageTile icon={Scissors} label="Serviços" value={countOf(person, "services")} />
            <UsageTile icon={Briefcase} label="Equipe" value={countOf(p, "employees")} />
            <UsageTile icon={CalendarDays} label="Na Agendix" value={formatRelative(p.createdAt).replace("há ", "") || "—"} />
          </div>

          <div className="divide-y divide-gray-100">
            <Section title="Plano e assinatura">
              <Row label="Plano em uso">
                <div className="flex flex-col items-end gap-0.5">
                  <PlanBadge plan={plan} meta={PLAN_META} />
                  <span className="text-[11px] font-normal text-gray-400">{getPlanSourceLabel(p)}</span>
                </div>
              </Row>
              <Row label="Assinatura">
                <StatusPill tone={p.subscription === "active" ? "green" : "gray"} dot>{p.subscription === "active" ? "Ativa" : "Inativa"}</StatusPill>
              </Row>
              {p.subscriptionPlan && <Row label="Produto contratado"><span className="break-all">{p.subscriptionPlan}</span></Row>}
              {p.subscription === "active" && getPaymentLabel(p) && <Row label="Pagamento via">{getPaymentLabel(p)}</Row>}
              {renewal && <Row label="Próxima renovação">{formatDate(renewal)} <span className="font-normal text-gray-400">· {renewal < new Date() ? "vencida" : `em ${Math.ceil((renewal - Date.now()) / 86400000)}d`}</span></Row>}
              {plan === "enterprise" && (
                <>
                  <Row label="Valor mensal">{p.enterprisePricing?.monthlyPrice != null ? formatCurrency(p.enterprisePricing.monthlyPrice) : null}</Row>
                  <Row label="Valor anual">{p.enterprisePricing?.annualPrice != null ? formatCurrency(p.enterprisePricing.annualPrice) : null}</Row>
                  {[
                    ["maxAppointmentsPerMonth", "Agendamentos/mês"],
                    ["maxClients", "Clientes"],
                    ["maxEmployees", "Funcionários"],
                    ["maxSharedAccounts", "Usuários e acessos"],
                  ].map(([key, label]) => <Row key={key} label={label}>{p.enterpriseLimits?.[key] ?? "Ilimitado"}</Row>)}
                </>
              )}
            </Section>

            <Section title="Negócio">
              <Row label="Nome">{p.businessName || null}</Row>
              <Row label="Áreas">
                {areas.length ? (
                  <div className="flex flex-wrap justify-end gap-1">
                    {areas.map((a) => <span key={a} className="rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{a}</span>)}
                  </div>
                ) : null}
              </Row>
              <Row label="Receita média">{p.averageRevenue != null ? formatCurrency(p.averageRevenue) : null}</Row>
              <Row label="Endereço">{p.address || p.addresses?.[0] || null}</Row>
            </Section>

            <Section title="Contato e aquisição">
              <Row label="E-mail"><span className="break-all">{p.email}</span></Row>
              <Row label="Telefone">{formatPhone(p.phone) || null}</Row>
              <Row label="Conheceu por">{ACQUISITION_LABELS[p.acquisitionSource] || null}</Row>
              {p.referralCodeUsed && <Row label="Código usado"><span className="font-mono text-xs">{p.referralCodeUsed}</span></Row>}
              {p.isAmbassador && p.ambassadorCode && <Row label="Código de embaixadora"><span className="font-mono text-xs">{p.ambassadorCode}</span></Row>}
            </Section>

            <Section title="Conta">
              <Row label="Criada em">{formatDate(p.createdAt)} <span className="font-normal text-gray-400">· {formatRelative(p.createdAt)}</span></Row>
              <Row label="Cargo">{ROLE_LABELS[p.role] || p.role}</Row>
              <Row label="Disparos">{p.dispatchesDisabled ? "Só push" : "WhatsApp, e-mail e push"}</Row>
              <Row label="Google Agenda">{p.google?.refresh_token || p.google?.access_token ? "Conectada" : "Não conectada"}</Row>
              <Row label="ID"><span className="break-all font-mono text-xs font-normal text-gray-400">{p._id}</span></Row>
            </Section>
          </div>
          {loading && <p className="px-5 pb-4 text-xs text-gray-400">Carregando detalhes...</p>}
        </>
      )}
    </Drawer>
  );
};
